import { useState, useEffect } from 'react';
import AdminPinDigitInput from './AdminPinDigitInput';

const AdminPinModal = ({ open, mode, onClose, onSubmit, addToast }) => {
  const [currentPin, setCurrentPin] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setCurrentPin("");
      setPin("");
      setConfirmPin("");
    }
  }, [open]);

  if (!open) return null;

  const needsConfirm = mode === "set" || mode === "reset" || mode === "change";

  const handleSave = async (e) => {
    e.preventDefault();
    if (mode === "change") {
      if (!/^\d{4,6}$/.test(currentPin) || !/^\d{4,6}$/.test(pin)) {
        addToast("PIN must be 4–6 digits.", "error");
        return;
      }
      if (pin !== confirmPin) {
        addToast("New PIN and Confirm PIN must match exactly.", "error");
        return;
      }
      if (currentPin === pin) {
        addToast("New PIN must be different from your current PIN.", "error");
        return;
      }
    } else if (!/^\d{4,6}$/.test(pin)) {
      addToast("PIN must be 4–6 digits.", "error");
      return;
    } else if (needsConfirm && pin !== confirmPin) {
      addToast("PIN and Confirm PIN must match exactly.", "error");
      return;
    }
    setBusy(true);
    try {
      if (mode === "change") {
        await onSubmit({ currentPin, newPin: pin }, mode);
      } else {
        await onSubmit(pin, mode);
      }
      onClose();
    } catch (err) {
      addToast(err.message || "PIN action failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "set"
    ? "Create approval PIN"
    : mode === "reset"
      ? "Reset approval PIN"
      : mode === "change"
        ? "Change approval PIN"
        : "Enter approval PIN";
  const hint = mode === "set"
    ? "New admins: choose a 4–6 digit PIN. Required when you approve a listing (Price Agreement → Approved)."
    : mode === "reset"
      ? "Forgot your PIN? Choose a new 4–6 digit PIN. This replaces your old one (we cannot show the stored PIN)."
      : mode === "change"
        ? "Enter your current PIN, then your new PIN (4–6 digits). Use the eye icon to show digits while typing."
        : "Enter the PIN you created. Use the eye icon to show digits while typing.";

  const submitLabel = mode === "set"
    ? "Create PIN"
    : mode === "reset"
      ? "Save new PIN"
      : mode === "change"
        ? "Update PIN"
        : mode === "approve"
          ? "Approve listing"
          : "Confirm";

  return (
    <div
      className="modal-overlay active"
      style={{ zIndex: 10000 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal admin-pin-modal" style={{ maxWidth: 420, padding: "1.85rem" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", marginBottom: "0.5rem" }}>
          <span style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(11, 31, 42, 0.1)", color: "var(--primary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="fas fa-key"></i>
          </span>
          <h3 style={{ margin: 0, fontSize: "1.2rem" }}>{title}</h3>
        </div>
        <p style={{ color: "var(--text-light)", fontSize: "0.9rem", marginBottom: "1.15rem", lineHeight: 1.5 }}>{hint}</p>
        {(mode === "approve" || mode === "verify") && (
          <p style={{ fontSize: "0.85rem", color: "var(--text-light)", margin: "-0.5rem 0 1rem", padding: "0.5rem 0.65rem", background: "rgba(11, 31, 42, 0.06)", borderRadius: 8 }}>
            <i className="fas fa-info-circle" style={{ marginRight: "0.35rem", color: "var(--primary)" }}></i>
            Your saved PIN cannot be revealed from the server. If you forgot it, cancel and use <strong>Reset PIN</strong> on the dashboard.
          </p>
        )}
        <form onSubmit={handleSave}>
          {mode === "change" && (
            <AdminPinDigitInput
              label="Current PIN"
              value={currentPin}
              onChange={setCurrentPin}
              autoComplete="current-password"
            />
          )}
          <AdminPinDigitInput
            label={mode === "change" ? "New PIN" : "PIN"}
            value={pin}
            onChange={setPin}
            autoComplete={mode === "set" || mode === "reset" || mode === "change" ? "new-password" : "current-password"}
          />
          {needsConfirm && (
            <AdminPinDigitInput
              label={mode === "change" ? "Confirm new PIN" : "Confirm PIN"}
              value={confirmPin}
              onChange={setConfirmPin}
              autoComplete="new-password"
            />
          )}
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? "Saving…" : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPinModal;
